<template>
    <div class="w-full flex divided" :class="tight ? 'py-12' : 'py-20'">
        <nuxt-link v-if="creator && creator._key" :to="$spoferanUrl(`${creator.model_name === 'user' ? 'athlete' : creator.model_name}s/${creator._key}`)" :aria-label="creator.display_name" class="h-32 relative mr-16">
            <avatar :model="creator" :alt="creator.display_name" :size="32"/>
			<avatar v-if="creator.club" :model="creator.club" :size="16" class="absolute bottom-0 right-0 mr--4 mb--4"/>
		</nuxt-link>
        <avatar v-else :model="creator" :size="34" class="mr-16"/>
        <div class="flex flex-column flex-grow-x-1">
            <div class="flex flex-column">
				<div class="flex justify-between">
					<nuxt-link v-if="creator._key" :to="$spoferanUrl(`${creator.model_name === 'user' ? 'athlete' : creator.model_name}s/${creator._key}`)" :style="{color: creator.color}" class="text-xs mb-2 leading-normal">{{ creator.display_name }}</nuxt-link>
					<span v-else class="text-xs mb-2 leading-normal">{{ creator.display_name }}</span>

					<lazy-dropdown v-if="editable" :menu-title="$t('label.comment')" left class="ml-4">
						<Icon :icon="icons.more" small color="gray-400" class="cursor-pointer hover:text-primary"/>
						<template #menu>
							<lazy-nav-list>
								<lazy-nav-list-item v-if="editable" @click="emit('edit')">{{ $t('action.edit') }}</lazy-nav-list-item>
								<lazy-nav-list-item v-if="editable">
									<lazy-form-link :action="updateUrl" method="DELETE"
													color="error"
													:confirm="{title: $t('confirm.comment.destroy.title'), message: $t('confirm.comment.destroy.message')}"
													@success="emit('destroyed')">
										{{ $t('action.destroy') }}
									</lazy-form-link>
								</lazy-nav-list-item>
							</lazy-nav-list>
						</template>
					</lazy-dropdown>
				</div>

                <div class="text-xs leading-wide whitespace-pre-wrap" v-html="displayText"/>
            </div>
            <div class="flex items-center justify-between mt-2">
				<dynamic-date :date="comment.created_at" color="gray-400" time relative xx-small/>

				<like-form :action="updateUrl + '/likes'" small
								:count="comment.likes_count"
								v-model:liked="hasLiked"
								:likes="comment.likes"
								ref="likeForm"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {transformUrlsToLinks} from "@spoferan/spoferan-ts-core";
import {DefaultUser} from "@spoferan/spoferan-ts-core";
import type {Comment} from "@spoferan/spoferan-ts-core";
import {moreIcon} from "../../../config";
import {ref, watch, computed} from 'vue';
import {useNuxtApp} from "#imports";

const {$auth} = useNuxtApp();

const icons = {
	more: moreIcon
};

const props = defineProps<{
	comment: Comment,
	updateUrl: string,
	tight?: boolean,
	liked?: boolean
}>();

const emit = defineEmits(['update:liked', 'edit', 'destroyed']);

const hasLiked = ref(props.liked ?? false);
watch(() => props.liked, (newValue) => {
	hasLiked.value = newValue;
});
watch(hasLiked, (newValue) => {
	if (newValue !== props.liked) {
		emit('update:liked', newValue);
	}
});

const creator = computed(() => {
	return props.comment.creator ?? DefaultUser;
});

const displayText = computed(() => {
	return props.comment.text ? transformUrlsToLinks(props.comment.text) : null;
});

const editable = computed(() => {
	if (!props.comment.creator || !$auth.user) {
		return;
	}

	return [$auth.user, ...$auth.user.actAs].find(actAs => actAs.model_type === props.comment.creator.model_type && actAs.id === props.comment.creator.id);
});

</script>